import Vue from 'vue';
import { toast } from '@mx/vix';
import { env } from './env';
import { runtime } from './envRuntime';
import { insertUrlParams } from '@/common/checkHandle';
import { monitor } from '@/common/monitor';

class Bridge {
  KNBP = KNB.getPromiseInstance();

  loginTime = 0;

  // 设置标题
  setTitle(title: string): void {
    document.title = title;
    KNB.ready(() => {
      KNB.setTitle({ title });
    });
  }

  setBounces(val: number) {
    if (!env.isHarmony) {
      KNB.ready(() => {
        KNB.setBouncesEnabled({
          enable: val, // 1表示允许反弹，0表示禁止。default: 1
        });
      });
    }
  }

  closePage(): void {
    const ehcModalFlag = env.queryStrings.ehcShowModal || '';
    const { pathname } = window.location;
    const isMainUrl = pathname.includes('index.html');
    // 解决余额半浮层返回在钱包页直接关闭钱包页面的问题
    if (ehcModalFlag && isMainUrl) {
      KNB.use('ehc.closeModal', {});
    } else {
      KNB.ready(() => {
        KNB.closePage({});
      });
    }
  }

  jumpPage(url: string) {
    if (!url) {
      return;
    }
    if (env.isNative) {
      if (env.isWechat || env.isWXMiniProgram || env.app === 'dianping-nova') {
        window.location.href = url;
      } else {
        KNB.ready(() => {
          KNB.jumpPage({
            url,
            query: {
              _knbopeninapp: 1,
            },
          });
        });
      }
    } else {
      window.location.replace(url);
    }
  }

  openPage(url: string, notitlebar: number = 0, success = () => {}) {
    // deleteUrlParams
    if (!url) {
      return;
    }
    if (env.isNative) {
      if (env.isWechat || env.isWXMiniProgram) {
        window.location.href = url;
      } else {
        KNB.ready(() => {
          KNB.openPage({
            url, // 需要打开的完整http链接
            // 用于配置容器行为参数，附加在跳转协议上
            query: {
              _knbopeninapp: 1,
              wkwebview: 1,
              noquery: 1,
              notitlebar,
            },
            success,
            fail: (e: any) => {
              monitor.captureMessage({
                name: 'KNB.openPage 失败',
                msg: { url, e },
              });
            },
          });
        });
      }
    } else {
      if (env.inWeixinMini) {
        url = insertUrlParams(url, 'token', env.token);
      }
      window.location.href = url;
    }
  }

  subscribe(action: string, handle: (data?: any) => void, success?: () => void) {
    KNB.ready(() => {
      KNB.subscribe({
        action,
        handle,
        // tslint:disable-next-line:no-empty
        success: () => {
          if (success) {
            success();
          }
        },
      });
    });
  }

  unSubscribe(action: string, success: () => void) {
    KNB.ready(() => {
      KNB.unsubscribe({
        action,
        success,
      });
    });
  }

  setNavigationBarHidden(flag = 1): void {
    KNB.ready(() => {
      KNB.setNavigationBarHidden({
        flag,
      });
    });
  }

  // 登录
  login(): void {
    this.loginTime += 1;

    if (this.loginTime > 1) {
      setTimeout(() => {
        toast.show('text', '登录失败，请重新登录美团');
      }, 0);
      return;
    }
    if (env.isNative) {
      KNB.ready(() => {
        KNB.logout({
          success: () => {
            this.knbLogin();
          },
        });
      });
    } else {
      this.webLogin();
    }
  }

  webLogin(): void {
    let loginAccountHost = 'https://passport.meituan.com/useraccount/ilogin';
    if (runtime >= 2) {
      loginAccountHost = 'https://passport.fe.test.sankuai.com/useraccount/ilogin';
    }
    window.location.href = `${loginAccountHost}?risk_partner=12&risk_app=216&risk_platform=3&backurl=${encodeURIComponent(
      window.location.href
    )}`;
  }

  knbLogin(): void {
    KNB.ready(() => {
      KNB.login({
        success: () => {
          window.location.reload();
        },
      });
    });
  }

  setStorage(key: string, value: string): void {
    KNB.ready(() => {
      if (KNB.setStorage) {
        KNB.setStorage({
          key,
          value,
          level: 0,
        });
      }
    });
  }

  getStorage(key: string): Promise<any> {
    return this.KNBP.getStorage({
      key,
    });
  }

  clearStorage(key: string): void {
    KNB.ready(() => {
      KNB.clearStorage({
        key,
        success: () => {},
        fail: () => {},
      });
    });
  }

  ehShow() {
    KNB.use('eh.show');
  }

  getUserInfo(): Promise<any> {
    return new Promise((resolve) => {
      KNB.ready(() => {
        KNB.getUserInfo({
          success: (res: any) => {
            if (res) {
              resolve(res);
            } else {
              resolve({});
            }
          },
          fail: (err) => {
            resolve({});
          },
        });
      });
      setTimeout(() => {
        resolve({});
      }, 1500);
    });
  }

  vibrate(): void {
    if (env.isNative) {
      KNB.ready(() => {
        KNB.vibrate({
          duration: 50,
        });
      });
    }
  }

  // 设置风控信息
  getFingerprint(): Promise<string> {
    if (env.isHarmony) {
      return Promise.resolve('');
    } else {
      return this.KNBP.getFingerprint().then((res: Record<string, string>) => res.fingerprint);
    }
  }

  setLLButton(icon: string, handle: () => void): Promise<any> {
    return this.KNBP.setLLButton({ icon, handle });
  }

  setLRButton(icon: string, handle: () => void): void {
    return this.KNBP.setLRButton({ icon, handle });
  }

  getService() {
    return this.KNBP.getServiceInfo({
      name: 'payment',
    }).then((res: any) => res.version);
  }

  subscribeAppear(handle: () => void, success?: () => void, fail?: () => void) {
    let subId = '';
    KNB.ready(() => {
      KNB.subscribe({
        action: 'appear',
        handle: () => {
          this.unsubscribeAppear(subId);
          handle();
        },
        success: (data) => {
          subId = data.subId;
          if (success) {
            success();
          }
        },
        fail: () => {
          if (fail) {
            fail();
          }
        },
      });
    });
  }

  unsubscribeAppear(subId?: string) {
    const param: Record<string, any> = {
      action: 'appear',
    };
    if (subId) param.subId = subId;
    KNB.ready(() => {
      KNB.unsubscribe(param);
    });
  }

  setAndroidStatusBar(color: string) {
    KNB.use('setStatusBarStyle', {
      style: 0,
      backgroundColor: color,
    });
  }

  setNavigationBar(bgColor: string, fontColor: string) {
    KNB.use('setNavigationBar', {
      backgroundColor: bgColor,
      color: fontColor,
    });
  }

  setLLButtonOfText(con: string) {
    KNB.setLLButton({
      type: 'text',
      text: con,
    });
  }

  setRRButtonOfText(con: string, handle: () => void) {
    KNB.setRRButton({
      type: 'text',
      text: con,
      handle,
    });
  }

  getLocation(): Promise<GetLocationResult> {
    return new Promise((resolve, reject) => {
      KNB.use('getLocation', {
        sceneToken: 'jf-4b58aa4469ef6adb',
        success(res: GetLocationResult) {
          resolve(res);
        },
        fail() {
          // eslint-disable-next-line
          reject("get location error");
        },
      });
    });
  }
}

export const bridge = new Bridge();

declare module 'vue/types/vue' {
  interface Vue {
    $bridge: Bridge;
  }
}
Vue.prototype.$bridge = bridge;

// 获取pgy组件需要的风控参数
export async function getCommonParams() {
  let fingerprint: string;
  let location: { lat: number; lng: number };
  try {
    fingerprint = await bridge.getFingerprint();
  } catch (error) {
    // 获取fingerprint失败 容错处理
    fingerprint = '';
  }
  try {
    const locationData = await bridge.getLocation();
    location = {
      lat: locationData.lat || 0,
      lng: locationData.lng || 0,
    };
  } catch (error) {
    // 获取location失败 容错处理
    location = {
      lat: 0,
      lng: 0,
    };
  }
  return new Promise((resolve, reject) => {
    try {
      const allData = {
        nb_fingerprint: fingerprint,
        nb_location: `${location.lat}_${location.lng}`,
      };
      resolve(allData);
    } catch (error) {
      // eslint-disable-next-line
      reject("数据获取失败，请重试");
    }
  });
}
